import { useEffect, useMemo, useState } from 'react';
import { upperCase } from 'lodash';
import { useUsersCsvDownloadQuery } from '@common/cache';
import { paths } from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { debounce } from '@util/debounce.ts';
import { ColumnNames } from '../types';
import {
  useUserTableStore,
  PersistedUserColumn,
  initialUserColumns,
} from '../Filters/filters.store';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box, Chip } from '@mui/material';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { getStatusColor, StatusLabels } from '@util/user';
import { ClipboardButton } from 'components/ClipboardButton';
import DiamondIcon from '@mui/icons-material/Diamond';
import { theme } from '@store';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';

export type IUserTableEntry =
  paths['/record/users']['get']['responses']['200']['content']['application/json']['users'][number];

const buildUserColumns = (isDense: boolean): GridColDef<IUserTableEntry>[] => [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 220,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <ClipboardButton textToCopy={`${params.row.email}`} size={16} />

        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <TruncatedTextWithConditionalTooltip
            width='100%'
            variant='body2'
            text={`${params.row.name} ${params.row.suffix || ''}`}
          />
          {!isDense && (
            <TruncatedTextWithConditionalTooltip
              width='100%'
              variant='caption'
              color='textSecondary'
              text={params.row.email}
            />
          )}
        </Box>
      </Box>
    ),
  },
  {
    field: 'role',
    headerName: 'Role',
    minWidth: 150,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={params.row.roles?.[0] || 'Role'}
      />
    ),
  },
  {
    field: 'title',
    headerName: 'Title',
    minWidth: 150,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.title || '-'}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'team',
    headerName: 'Team',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params =>
      params.row.team ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <DiamondIcon
            sx={{
              width: 24,
              height: 24,
              marginRight: 1,
              color: params.row.team_color,
            }}
          />
          <Box
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={params.row.team}
              width='100%'
              variant='body2'
            />
            {!isDense && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.team_description}
                variant='caption'
                width='100%'
                color='textSecondary'
              />
            )}
          </Box>
        </Box>
      ) : (
        '-'
      ),
  },
  {
    field: 'supervisor',
    headerName: 'Supervisor',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params =>
      params.row.supervisor ? (
        <TruncatedTextWithConditionalTooltip
          text={params.row.supervisor}
          variant='body2'
          width='100%'
        />
      ) : (
        <TruncatedTextWithConditionalTooltip
          text='No supervisor'
          variant='body2'
          width='100%'
          sx={{ color: theme.palette.semiBlack.main }}
        />
      ),
  },
  {
    field: 'assigned_entities',
    headerName: 'Assigned Entity',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.assigned_entities?.[0]?.name}
          variant='body2'
          width='100%'
        />
        {!isDense && (
          <TruncatedTextWithConditionalTooltip
            variant='caption'
            color='textSecondary'
            text={params.row.assigned_entities?.[0]?.type}
            width='100%'
          />
        )}
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    minWidth: 150,
    sortable: false,
    filterable: false,
    headerAlign: 'center',
    renderCell: params => (
      <Chip
        sx={{
          backgroundColor: getStatusColor(params.row.status),
          color: theme => theme.palette.common.white,
          mx: 'auto',
          height: '24px',
        }}
        label={StatusLabels[params.row.status] || 'Status unknown'}
      />
    ),
  },
  {
    field: 'last_active',
    headerName: 'Last Active',
    width: 160,
    minWidth: 160,
    sortable: true,
    filterable: false,
    renderCell: params => {
      const lastActive = params.row.last_active
        ? new Date(params.row.last_active)
        : undefined;
      return lastActive ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <TruncatedTextWithConditionalTooltip
            width='100%'
            variant='body2'
            text={`${lastActive.toLocaleString('en-US', { month: 'short' })} ${lastActive.getDate()}, ${lastActive.getFullYear()}`}
          />
          {!isDense && (
            <TruncatedTextWithConditionalTooltip
              variant='caption'
              width='100%'
              color='textSecondary'
              text={`${lastActive.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })}`}
            />
          )}
        </Box>
      ) : (
        '-'
      );
    },
  },
];

export const useUserTable = () => {
  const {
    filters,
    setFilters,
    isFiltersCardOpen,
    clearStore,
    syncDraftWithAppliedFilters,
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useUserTableStore();
  const { sort_by, sort_order } = filters;
  const apiRef = useGridApiRef();
  const [searchQuery, setSearchQuery] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const { data, isPending, mutateAsync } = useUsersCsvDownloadQuery();

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users`;
      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }
      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  useEffect(() => {
    return () => {
      // On page leave, clear all applied filters (and discard unsaved draft
      // edits) while keeping the current sorting. Pinned columns and column
      // order are persisted separately and are left untouched.
      syncDraftWithAppliedFilters();
    };
  }, []);

  const handleDownload = async () => {
    const loadingToast = toast.loading('Downloading Users Table...', {
      isLoading: true,
      autoClose: 500,
    });
    try {
      await mutateAsync({
        sort_by: filters.sort_by as ColumnNames & undefined,
        sort_order: upperCase(filters.sort_order) as 'ASC' | 'DESC',
        ...(filters.search !== '' && { search: filters.search }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(filters.role !== '' && { role: filters.role as any }),
        ...(filters.assigned_entity_id !== '' && {
          assigned_entity_id: filters.assigned_entity_id,
        }),
        ...(filters.status !== '' && {
          status: filters.status as
            | 'active'
            | 'deactivated'
            | 'invitation_pending'
            | 'invitation_expired'
            | 'password_reset'
            | 'missing_mfa'
            | undefined,
        }),
      });
      toast.success('Downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ ...filters, sort_by: 'name', sort_order: 'asc' });
      return;
    }
    setFilters({ ...filters, sort_by: field as ColumnNames, sort_order: sort });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ ...filters, limit: model.pageSize, page: model.page + 1 });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    debounce(
      () => {
        setFilters({ ...filters, search: event.target.value, page: 1 });
      },
      true,
      300,
    );
  };

  const runtimeColumns = useMemo(
    () => buildUserColumns(density === 'dense'),
    [density],
  );
  const columns = buildGridColumns<IUserTableEntry, PersistedUserColumn>(
    runtimeColumns,
    persistedColumns,
  );

  const toPersistedColumn = (
    col: GridColDef<IUserTableEntry>,
  ): PersistedUserColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    setHiddenColumns(
      columns.map(col => col.field).filter(field => model[field] === false),
    );
  };

  const MIN_VISIBLE_COLUMNS = 4;
  const visibleColumnsCount = Object.values(columnVisibilityModel).filter(
    Boolean,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;
  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  const handleResetAll = () => {
    clearStore();
    setSearchQuery('');
    resetView();
  };

  return {
    filters,
    columns: columnsWithHideable,
    isFiltersCardOpen,
    handleChangeOrder,
    onPageModelChange,
    handleSearchChange,
    sort_by,
    sort_order,
    handleDownload,
    isDownloading: isPending,
    searchQuery,
    setSearchQuery,
    handleResetAll,
    initialSortFilters: { sort_order: 'asc', sort_by: 'name' },
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange: setDensity,
    initialColumns: initialUserColumns,
    apiRef,
  };
};
